import { useEffect, useRef } from 'react';
import type { SceneLogEntry, StoryPack } from '@plotjack/core';
import { trackLabel } from '@plotjack/core';

interface Props {
  open: boolean;
  story: StoryPack;
  log: SceneLogEntry[];
  current?: { title: string; goal?: string };
  onClose: () => void;
}

const PLAN_TEXT: Record<SceneLogEntry['plan'], string> = {
  blocked: "You blocked the narrator's plan.",
  delayed: "You delayed the narrator's plan.",
  bent: "You bent the narrator's plan off course.",
  stands: "The narrator's plan went ahead anyway.",
  untouched: 'You let the scene play out.',
};

export function StoryLog({ open, story, log, current, onClose }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div className="modal-card story-log" role="dialog" aria-modal="true" aria-labelledby="story-log-title" onClick={(e) => e.stopPropagation()}>
        <h2 id="story-log-title">Story so far</h2>
        <p className="story-log__premise">{story.premise}</p>
        <ol className="story-log__list">
          {log.map((e) => (
            <li key={e.sceneIndex}>
              <p className="story-log__title">{e.title}</p>
              <p>{e.summary}</p>
              {e.plays.length > 0 && (
                <ul className="story-log__plays">
                  {e.plays.map((p, i) => (
                    <li key={i}>
                      <strong>{p.cardName}:</strong> {p.text}
                    </li>
                  ))}
                </ul>
              )}
              <p className="story-log__meta">
                {PLAN_TEXT[e.plan]}
                {e.dreadDelta ? ` Dread ${e.dreadDelta > 0 ? '+' : ''}${e.dreadDelta}.` : ''}
                {(['hope', 'chaos', 'fate'] as const)
                  .filter((t) => e.trackDeltas[t])
                  .map((t) => ` ${trackLabel(story, t)} ${e.trackDeltas[t]! > 0 ? '+' : ''}${e.trackDeltas[t]}.`)
                  .join('')}
              </p>
            </li>
          ))}
          {current && (
            <li className="story-log__current">
              <p className="story-log__title">Now: {current.title}</p>
              {current.goal && <p>{current.goal}</p>}
            </li>
          )}
        </ol>
        <div className="modal-actions">
          <button ref={closeRef} type="button" className="btn btn-primary" onClick={onClose}>
            Back to the story
          </button>
        </div>
      </div>
    </div>
  );
}
